import { useCallback, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { Controller, useForm } from 'react-hook-form';
import DatePicker from 'react-datepicker';
import { addHours } from 'date-fns';
import 'react-datepicker/dist/react-datepicker.css';

import {
  SET_LOADING,
  SET_SUCCESS,
  SET_ON_SUBMIT,
} from '../../reducers/actionReducer';
import LoadingSpinner from '../LoadingSpinner';
import ErrorText from '../ErrorText';
import { useAuth } from '../../hooks/useAuth';
import { SCHEDULE_MAINTENANCE } from '../../mutations';
import FormItem from '../form/FormItem';
import DatePickerInput from '../inputs/DatePickerInput';
import SelectInput from '../inputs/SelectInput';
import { MAINTENANCES_QUERY } from '../../queries';

const DURATION_OPTIONS = [
  { name: 'Custom', value: 'custom' },
  { name: '1 Hour', value: '1' },
  { name: '2 Hours', value: '2' },
  { name: '4 Hours', value: '4' },
  { name: '8 Hours', value: '8' },
  { name: '24 Hours', value: '24' },
];

export default function ScheduleMaintenance({ dispatchAction }) {
  const { getUser } = useAuth();

  const { handleSubmit, control, watch, setValue } = useForm({
    defaultValues: {
      startDate: new Date(),
      endDate: null,
      duration: 'custom',
    },
  });

  const startDate = watch('startDate');
  const duration = watch('duration');

  useEffect(() => {
    if (duration !== 'custom' && startDate) {
      const hours = parseInt(duration, 10);
      setValue('endDate', addHours(startDate, hours));
    }
  }, [duration, startDate, setValue]);

  const [scheduleMaintenance, { loading, error, called, data }] = useMutation(
    SCHEDULE_MAINTENANCE,
    {
      refetchQueries: [MAINTENANCES_QUERY],
    }
  );

  const onSubmit = useCallback(() => {
    handleSubmit((data) => {
      const { duration, ...submitData } = data;
      scheduleMaintenance({
        variables: {
          ...submitData,
        },
      });
    })();
  }, [handleSubmit, scheduleMaintenance]);

  useEffect(() => {
    if (scheduleMaintenance) {
      dispatchAction({
        type: SET_ON_SUBMIT,
        payload: {
          onSubmit,
        },
      });
    }
  }, [dispatchAction, handleSubmit, onSubmit, scheduleMaintenance]);

  useEffect(() => {
    if (loading !== undefined) {
      dispatchAction({
        type: SET_LOADING,
        payload: { loading },
      });
    }
  }, [dispatchAction, loading]);

  useEffect(() => {
    if (called && data && !error) {
      dispatchAction({
        type: SET_SUCCESS,
        payload: {
          message: 'Maintenance scheduled successfully.',
        },
      });
    }
  }, [called, data, dispatchAction, error, getUser]);

  return (
    <div className="flex flex-col justify-center items-center text-center space-y-6 w-full px-24">
      {loading && <LoadingSpinner message="Scheduling..." />}
      {error && <ErrorText message={error.message} />}
      {!loading && !error && (
        <form
          onSubmit={onSubmit}
          className="flex flex-col justify-start text-left space-y-4 w-80"
        >
          <FormItem label="Start Date" htmlFor="startDate">
            <Controller
              control={control}
              name="startDate"
              rules={{ required: 'Start Date is required.' }}
              render={({
                field: { value, ...restField },
                fieldState: { error },
              }) => (
                <DatePicker
                  {...restField}
                  selected={value}
                  showTimeSelect
                  timeIntervals={15}
                  timeCaption="Time"
                  dateFormat="MM/dd/yyyy h:mm aa"
                  customInput={<DatePickerInput error={error} />}
                />
              )}
            />
          </FormItem>

          <FormItem label="Duration" htmlFor="duration">
            <Controller
              control={control}
              name="duration"
              render={({ field: { value, onChange } }) => (
                <SelectInput
                  value={value}
                  onChange={onChange}
                  options={DURATION_OPTIONS}
                />
              )}
            />
          </FormItem>

          <FormItem
            label="End Date"
            htmlFor="endDate"
            helpText="Leave blank for an indefinite period of maintenance."
          >
            <Controller
              control={control}
              name="endDate"
              render={({ field: { value, ...restField } }) => (
                <DatePicker
                  {...restField}
                  selected={value}
                  showTimeSelect
                  timeIntervals={15}
                  timeCaption="Time"
                  dateFormat="MM/dd/yyyy h:mm aa"
                  customInput={<DatePickerInput />}
                  onChange={(date) => {
                    setValue('duration', 'custom');
                    restField.onChange(date);
                  }}
                />
              )}
            />
          </FormItem>
        </form>
      )}
    </div>
  );
}
